import React, { useEffect, useMemo, useState } from "react";

import { Segment } from "semantic-ui-react";

import moment from "moment";
import Viewer from "react-viewer";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import ModalEditImage from "react-lib/apps/common/ModalEditImage";
import ModInfo from "react-lib/apps/common/ModInfo";
import SnackMessage from "react-lib/apps/common/SnackMessage";

import CardRehabHistoryUX from "./CardRehabHistoryUX";
import TableHistory from "./TableHistory";

import {
  formatRehabImage,
  RehabHistoryDetailType,
  RehabOrderGroupType,
  RehabOrderType,
} from "./RHBInterface";

import { formatDate, formatDatetime } from "react-lib/utils/dateUtils";

type CardRehabHistoryProps = {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: () => void) => any;
  rehabHistoryDetail?: RehabHistoryDetailType;
  rehabHistoryList?: RehabOrderType[];
  viewRehabHistoryId?: number;
  buttonLoadCheck: any;
  errorMessage: any;
  languageUX: string;
  selectedPatient?: any;
};

const BUTTON_ACTIONS = {
  print: "PRINT",
  search: "SEARCH",
};

const CARD_REHAB_HISTORY = "CardRehabHistory";

const ACTION_SEARCH = `${CARD_REHAB_HISTORY}_${BUTTON_ACTIONS.search}`;

const ACTION_PRINT = `${CARD_REHAB_HISTORY}_${BUTTON_ACTIONS.print}`;

const currentDate = formatDate(moment());
const thirtyDaysAgo = formatDate(moment().add(-30, "days"));

const CardRehabHistory = (props: CardRehabHistoryProps) => {
  const [activePanes, setActivePanes] = useState<Record<string, any>[]>([]);
  const [imageOverlay, setImageOverlay] = useState<Record<string, any>[]>([]);

  const [openModalEditImage, setOpenModalEditImage] = useState<boolean>(false);
  const [filter, setFilter] = useState({ endDate: currentDate, startDate: thirtyDaysAgo });

  const [modDocumentScan, setModDocumentScan] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    if (props.selectedPatient) {
      props.onEvent({
        message: "getRhbHistory",
        params: { btnAction: ACTION_SEARCH, card: CARD_REHAB_HISTORY, filter },
      });
    }

    return () => {
      props.onEvent({
        message: "clearRhbHistory",
        params: {},
      });
    };
  }, [props.selectedPatient?.id]);

  useEffect(() => {
    if (modDocumentScan?.images.length === 0) {
      setModDocumentScan({ ...modDocumentScan, images: [{ src: modDocumentScan.url }] });
    }
  }, [modDocumentScan]);

  const treatmentOrders = useMemo(() => {
    const items = props.rehabHistoryDetail?.items ?? [];

    return items.flatMap((item) =>
      item.treatments.map((treatment) => treatment.plan_item_detail?.name)
    );
  }, [props.rehabHistoryDetail]);

  const secondaryProcedure = useMemo(() => {
    const items = props.rehabHistoryDetail?.secondary_procedure ?? [];

    return items.map((item) => `[${item.icd9cm_code}] ${item.icd9cm_term}`);
  }, [props.rehabHistoryDetail]);

  const documentScan = useMemo(() => {
    const documents = props.rehabHistoryDetail?.documents ?? [];
    const scannedDocNames = props.rehabHistoryDetail?.data.ScannedDocument_name ?? {};

    return documents.map((item) => {
      const regex = /\/([^/]+?\.[^/.]+)\?/;
      const match = regex.exec(item.document_image);
      const name = scannedDocNames[item.id ?? ""] || match?.[1];

      return { name, url: item.document_image };
    });
  }, [props.rehabHistoryDetail]);

  const handleSearch = () => {
    props.onEvent({
      message: "getRhbHistory",
      params: { btnAction: ACTION_SEARCH, card: CARD_REHAB_HISTORY, filter },
    });
  };

  const handleOpenGallery = (images: any[]) => {
    const formattedImage = formatRehabImage(images);

    setImageOverlay(formattedImage.imageOverlay);
    setActivePanes(formattedImage.activePanes);
    setOpenModalEditImage(true);
  };

  const handleCloseEditImage = () => {
    setImageOverlay([]);
    setActivePanes([]);
    setOpenModalEditImage(false);
  };

  const handleChangeDate = (key: string) => (value: string) => {
    setFilter({ ...filter, [key]: value });
  };

  const handleViewDetail = (data: RehabOrderGroupType, btnAction: string) => {
    props.onEvent({
      message: "selectRehabHistory",
      params: {
        btnAction,
        data,
        onSuccess: handleScrollToPosition,
      },
    });
  };

  const handleScrollToPosition = () => {
    const header = document.querySelector("#CardRehabHistoryUX-div-81") as HTMLElement | null;
    const content = document.querySelector("#tab-mainMenu ~ div") as HTMLElement | null;

    if (!header || !content) {
      return;
    }

    // คำนวณตำแหน่งของ header เทียบกับ content
    const headerRect = header.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();

    // หาตำแหน่งที่จะ scroll ใน content
    const scrollToPosition = headerRect.top - contentRect.top + content.scrollTop;

    // ทำการ scroll
    content.scrollTo({
      behavior: "smooth",
      top: scrollToPosition,
    });
  };

  const handleOpenModDocumentScan = (data: Record<string, any>) => () => {
    setModDocumentScan({ ...data, images: [] });
  };

  const handleCloseModDocumentScan = () => {
    setModDocumentScan(null);
  };

  const handleCloseDetail = () => {
    props.setProp("rehabHistoryDetail", null);
  };

  const handlePrintReport = () => {
    props.onEvent({
      message: "printRhbPTCertificateForm",
      params: { btnAction: ACTION_PRINT, documentScan, secondaryProcedure, treatmentOrders },
    });
  };

  console.log("CardRehabHistory props:", props.rehabHistoryDetail);

  return (
    <>
      <SnackMessage
        error={props.errorMessage?.[CARD_REHAB_HISTORY]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_REHAB_HISTORY}`, null);
        }}
      />

      <CardRehabHistoryUX
        data={props.rehabHistoryDetail}
        filter={filter}
        performEndDate={formatDatetime(props.rehabHistoryDetail?.finish)}
        performStartDate={formatDatetime(props.rehabHistoryDetail?.start)}
        languageUX={props.languageUX}
        onChangeDate={handleChangeDate}
        onClose={handleCloseDetail}
        buttonPrintReport={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="blue"
            icon="print"
            paramKey={ACTION_PRINT}
            size="mini"
            title="Physical Therapy Certificate"
            buttonLoadCheck={props.buttonLoadCheck?.[ACTION_PRINT]}
            onClick={handlePrintReport}
          />
        }
        buttonSearch={
          <ButtonLoadCheck
            setProp={props.setProp}
            color="blue"
            paramKey={ACTION_SEARCH}
            size="small"
            title="SEARCH"
            buttonLoadCheck={props.buttonLoadCheck?.[ACTION_SEARCH]}
            onClick={handleSearch}
          />
        }
        documentScan={
          documentScan.length === 0
            ? "No documents found"
            : documentScan.map((item) => (
                <div
                  key={`doc-${item.url}`}
                  style={{
                    color: "rgba(33, 133, 208, 1)",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                  onClick={handleOpenModDocumentScan(item)}
                >
                  {item.name}
                </div>
              ))
        }
        medications={
          (props.rehabHistoryDetail?.medications ?? []).length === 0 ? (
            "No medications found"
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "auto max-content 1fr",
                marginLeft: "0.95rem",
              }}
            >
              {props.rehabHistoryDetail?.medications.map((item, index) => (
                <React.Fragment key={`medication-${index}`}>
                  <div
                    style={{
                      backgroundColor: "rgba(0,0,0,.87)",
                      borderRadius: "500rem",
                      height: "4px",
                      margin: "auto",
                      width: "4px",
                    }}
                  />
                  <label style={{ paddingLeft: "0.5rem", paddingRight: "3rem" }}>
                    {item.name} {item.label}{" "}
                  </label>
                  <label>
                    จำนวน {item.quantity} {item.unit_name}
                  </label>
                </React.Fragment>
              ))}
            </div>
          )
        }
        secondaryProcedure={
          secondaryProcedure.length === 0 ? (
            "-"
          ) : (
            <div>
              {secondaryProcedure.map((name, index) => (
                <div key={`secondary-procedure-${index}`}>{name}</div>
              ))}
            </div>
          )
        }
        table={
          <TableHistory
            setProp={props.setProp}
            rehabHistoryDetail={props.rehabHistoryDetail}
            rehabHistoryList={props.rehabHistoryList}
            viewRehabHistoryId={props.viewRehabHistoryId}
            buttonLoadCheck={props.buttonLoadCheck}
            onOpenGallery={handleOpenGallery}
            onViewDetail={handleViewDetail}
          />
        }
        treatmentOrders={
          treatmentOrders.length === 0 ? (
            "-"
          ) : (
            <ul style={{ paddingLeft: "2rem" }}>
              {treatmentOrders.map((name, index) => (
                <li key={`treatment-order-${index}`}>{name}</li>
              ))}
            </ul>
          )
        }
      />

      <ModalEditImage
        activePanes={activePanes}
        cancelImage={handleCloseEditImage}
        hiddenTab={false}
        hideBabyTooth={false}
        imageOverlay={imageOverlay}
        openEditModal={openModalEditImage}
        tabAddControl={null}
        readOnly
        onCloseModal={handleCloseEditImage}
      />

      <ModInfo
        style={{ height: "fit-content", width: "fit-content" }}
        backgroundColor="var(--primary-theme-color)"
        buttonStyle={{ display: "none" }}
        open={!!modDocumentScan}
        titleName={"\u00A0"}
        closeIcon
        onClose={handleCloseModDocumentScan}
      >
        <Segment
          id="viewer-container"
          style={{
            fontWeight: "bold",
            height: "calc(100vh - 10rem)",
            width: "37.5rem",
          }}
          color="grey"
          inverted
        >
          <Viewer
            container={document.querySelector("#viewer-container") as HTMLElement}
            images={modDocumentScan?.images}
            noClose
            visible
            onClose={handleCloseModDocumentScan}
          />
        </Segment>
      </ModInfo>
    </>
  );
};

CardRehabHistory.displayName = "CardRehabHistory";

export default React.memo(CardRehabHistory);
